import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Timesheet,
  TimeEntry,
  TimesheetStatus,
  BillingCode,
  CreateTimesheetData,
  PayrollExportData,
} from '../models/Timesheet';
import { SessionNote } from '../models/SessionNote';
import { travelService } from './TravelService';
import { clientService } from './ClientService';

class TimesheetService {
  private timesheetCollection = collection(db, 'timesheets');

  /**
   * Create a new timesheet for a user and period
   */
  async createTimesheet(data: CreateTimesheetData): Promise<string> {
    const timesheetData: Omit<Timesheet, 'id'> = {
      companyId: '', // Will be set by caller
      userId: data.userId,
      userName: data.userName,
      userRole: data.userRole,
      employeeId: data.employeeId,
      employmentType: data.employmentType,
      periodStart: Timestamp.fromDate(data.periodStart),
      periodEnd: Timestamp.fromDate(data.periodEnd),
      timeEntries: [],
      totalBillableHours: 0,
      totalBreakHours: 0,
      totalHours: 0,
      hoursByBillingCode: {},
      status: 'draft',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      lastEditedBy: data.userId,
    };

    const docRef = await addDoc(this.timesheetCollection, timesheetData);
    return docRef.id;
  }

  /**
   * Generate timesheet from session notes for a given period
   */
  async generateTimesheetFromSessionNotes(
    companyId: string,
    userId: string,
    userName: string,
    userRole: string,
    periodStart: Date,
    periodEnd: Date,
    sessionNotes: SessionNote[]
  ): Promise<string> {
    const timeEntries: TimeEntry[] = [];
    let totalBillableMinutes = 0;
    let totalBreakMinutes = 0;
    const hoursByCode: { [key in BillingCode]?: number } = {};

    // Resolve client display names once per unique client so supervisors can see
    // which client each entry is for (required before Phase 2 approval).
    const uniqueClientIds = Array.from(
      new Set(sessionNotes.map((n) => n.clientId).filter(Boolean))
    );
    const clientNameById = new Map<string, string>();
    await Promise.all(
      uniqueClientIds.map(async (cid) => {
        try {
          const client = await clientService.getClient(cid);
          if (client) {
            clientNameById.set(cid, `${client.firstName} ${client.lastName}`.trim());
          }
        } catch {
          // Leave unresolved — the entry falls back to '' and the UI shows "Client".
        }
      })
    );

    // Convert session notes to time entries
    for (const note of sessionNotes) {
      if (!note.timestamps.sessionStart || !note.timestamps.sessionEnd) {
        continue; // Skip incomplete sessions
      }

      const sessionStart = note.timestamps.sessionStart;
      const sessionEnd = note.timestamps.sessionEnd;
      const checkIn = note.timestamps.sessionCheckIn || sessionStart;

      // Calculate duration in minutes
      const startMillis = sessionStart.toMillis();
      const endMillis = sessionEnd.toMillis();
      const durationMinutes = Math.round((endMillis - startMillis) / 60000);

      // Get billing code from supervision data or default to 97156 for RBTs
      const billingCode: BillingCode = note.supervision?.billingCode as BillingCode ||
        (userRole === 'rbt' ? '97156' : '97155');

      const timeEntry: TimeEntry = {
        id: `${note.id}_entry`,
        sessionNoteId: note.id,
        appointmentId: note.appointmentId,
        clientId: note.clientId,
        clientName: clientNameById.get(note.clientId) || '',

        checkInTime: checkIn,
        sessionStartTime: sessionStart,
        sessionEndTime: sessionEnd,
        checkOutTime: note.timestamps.sessionEnd,

        billableMinutes: durationMinutes,
        totalMinutes: durationMinutes,

        billingCode,
        locationVerified: !!note.checkInLocation,
        checkInLocation: note.checkInLocation ? {
          latitude: note.checkInLocation.latitude,
          longitude: note.checkInLocation.longitude,
          accuracy: note.checkInLocation.accuracy,
        } : undefined,

        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      timeEntries.push(timeEntry);
      totalBillableMinutes += durationMinutes;

      // Track hours by billing code
      const hours = durationMinutes / 60;
      hoursByCode[billingCode] = (hoursByCode[billingCode] || 0) + hours;
    }

    // Mileage comes from explicit, destination-tied travel trips (the honest
    // two-tap "On my way / I'm here" capture) — NOT inferred from consecutive
    // session pins (which was gameable). Sum the reimbursable completed trips for
    // this user in the period and attach each trip's amount to the matching
    // session entry by appointmentId. Trips are already priced at the
    // effective-dated rate in effect on their travel date (see TravelService).
    let totalMileage = 0;
    let totalMileageReimbursement = 0;
    const trips = await travelService.getTripsByRbtAndPeriod(userId, periodStart, periodEnd);
    const entryByAppt = new Map(timeEntries.map((e) => [e.appointmentId, e]));
    for (const trip of trips) {
      if (trip.classification !== 'reimbursable') continue; // commute legs excluded
      const miles = trip.distance || 0;
      const amount = trip.reimbursementAmount || 0;
      totalMileage += miles;
      totalMileageReimbursement += amount;
      const entry = entryByAppt.get(trip.appointmentId);
      if (entry) {
        entry.mileage = {
          distance: miles,
          rate: trip.rateApplied,
          reimbursementAmount: amount,
        };
      }
    }
    totalMileage = Math.round(totalMileage * 100) / 100;
    totalMileageReimbursement = Math.round(totalMileageReimbursement * 100) / 100;

    // Create timesheet
    const timesheetData: Omit<Timesheet, 'id'> = {
      companyId,
      userId,
      userName,
      userRole,
      periodStart: Timestamp.fromDate(periodStart),
      periodEnd: Timestamp.fromDate(periodEnd),
      timeEntries,
      totalBillableHours: totalBillableMinutes / 60,
      totalBreakHours: totalBreakMinutes / 60,
      totalHours: totalBillableMinutes / 60,
      totalMileage,
      totalMileageReimbursement,
      hoursByBillingCode: hoursByCode,
      status: 'draft',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      lastEditedBy: userId,
    };

    const docRef = await addDoc(this.timesheetCollection, timesheetData);
    return docRef.id;
  }

  /**
   * Get timesheet by ID
   */
  async getTimesheetById(timesheetId: string): Promise<Timesheet | null> {
    const docRef = doc(this.timesheetCollection, timesheetId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return {
      id: docSnap.id,
      ...docSnap.data(),
    } as Timesheet;
  }

  /**
   * Get all timesheets for a user
   */
  async getTimesheetsByUser(userId: string): Promise<Timesheet[]> {
    const q = query(
      this.timesheetCollection,
      where('userId', '==', userId),
      orderBy('periodStart', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as Timesheet));
  }

  /**
   * Get timesheets by status (for approval queue)
   */
  async getTimesheetsByStatus(
    companyId: string,
    status: TimesheetStatus
  ): Promise<Timesheet[]> {
    const q = query(
      this.timesheetCollection,
      where('companyId', '==', companyId),
      where('status', '==', status),
      orderBy('submittedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as Timesheet));
  }

  /**
   * Get timesheets for a specific period
   */
  async getTimesheetsByPeriod(
    companyId: string,
    periodStart: Date,
    periodEnd: Date
  ): Promise<Timesheet[]> {
    const q = query(
      this.timesheetCollection,
      where('companyId', '==', companyId),
      where('periodStart', '>=', Timestamp.fromDate(periodStart)),
      where('periodEnd', '<=', Timestamp.fromDate(periodEnd)),
      orderBy('periodStart', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as Timesheet));
  }

  /**
   * Submit timesheet for approval
   */
  async submitTimesheet(timesheetId: string, userId: string): Promise<void> {
    const docRef = doc(this.timesheetCollection, timesheetId);
    await updateDoc(docRef, {
      status: 'submitted',
      submittedAt: Timestamp.now(),
      submittedBy: userId,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Approve timesheet
   */
  async approveTimesheet(
    timesheetId: string,
    approverId: string,
    approverName: string,
    approverRole: string
  ): Promise<void> {
    const docRef = doc(this.timesheetCollection, timesheetId);
    await updateDoc(docRef, {
      status: 'approved',
      approvedAt: Timestamp.now(),
      approvedBy: approverId,
      approverName,
      approverRole,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Reject timesheet with reason
   */
  async rejectTimesheet(
    timesheetId: string,
    rejectorId: string,
    reason: string
  ): Promise<void> {
    const docRef = doc(this.timesheetCollection, timesheetId);
    await updateDoc(docRef, {
      status: 'rejected',
      rejectedAt: Timestamp.now(),
      rejectedBy: rejectorId,
      rejectionReason: reason,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Mark timesheet as paid
   */
  async markAsPaid(
    timesheetId: string,
    payrollBatchId?: string
  ): Promise<void> {
    const docRef = doc(this.timesheetCollection, timesheetId);
    await updateDoc(docRef, {
      status: 'paid',
      paidAt: Timestamp.now(),
      payrollBatchId,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Raise dispute on timesheet
   */
  async raiseDispute(
    timesheetId: string,
    employeeNotes: string
  ): Promise<void> {
    const docRef = doc(this.timesheetCollection, timesheetId);
    await updateDoc(docRef, {
      disputeRaised: true,
      employeeNotes,
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Resolve dispute
   */
  async resolveDispute(
    timesheetId: string,
    resolution: string,
    adminNotes?: string
  ): Promise<void> {
    const docRef = doc(this.timesheetCollection, timesheetId);
    await updateDoc(docRef, {
      disputeRaised: false,
      disputeResolution: resolution,
      adminNotes: adminNotes || '',
      updatedAt: Timestamp.now(),
    });
  }

  /**
   * Calculate overtime hours for W2 employees (over 40 hours per week)
   */
  calculateOvertimeHours(totalHours: number): { regular: number; overtime: number } {
    const regularLimit = 40;
    if (totalHours <= regularLimit) {
      return { regular: totalHours, overtime: 0 };
    }
    return {
      regular: regularLimit,
      overtime: totalHours - regularLimit,
    };
  }

  /**
   * Export timesheet data for payroll
   */
  async exportForPayroll(timesheetId: string): Promise<PayrollExportData> {
    const timesheet = await this.getTimesheetById(timesheetId);
    if (!timesheet) {
      throw new Error('Timesheet not found');
    }

    const { regular, overtime } = this.calculateOvertimeHours(timesheet.totalHours);

    const billingCodeBreakdown = Object.entries(timesheet.hoursByBillingCode).map(
      ([code, hours]) => ({
        code,
        hours: hours || 0,
      })
    );

    return {
      employeeId: timesheet.employeeId || timesheet.userId,
      employeeName: timesheet.userName,
      periodStart: timesheet.periodStart.toDate().toISOString(),
      periodEnd: timesheet.periodEnd.toDate().toISOString(),
      totalHours: timesheet.totalHours,
      regularHours: regular,
      overtimeHours: overtime,
      totalMileageReimbursement: timesheet.totalMileageReimbursement || 0,
      billingCodeBreakdown,
    };
  }

  /**
   * Export multiple timesheets to CSV format
   */
  async exportToCSV(timesheetIds: string[]): Promise<string> {
    const timesheets = await Promise.all(
      timesheetIds.map(id => this.exportForPayroll(id))
    );

    // CSV header
    const header = [
      'Employee ID',
      'Employee Name',
      'Period Start',
      'Period End',
      'Regular Hours',
      'Overtime Hours',
      'Total Hours',
      'Mileage Reimbursement',
      'Billing Codes',
    ].join(',');

    // CSV rows
    const rows = timesheets.map(t => {
      const billingCodes = t.billingCodeBreakdown
        .map(b => `${b.code}:${b.hours}h`)
        .join('; ');

      return [
        t.employeeId,
        `"${t.employeeName}"`,
        t.periodStart.split('T')[0],
        t.periodEnd.split('T')[0],
        t.regularHours.toFixed(2),
        t.overtimeHours.toFixed(2),
        t.totalHours.toFixed(2),
        t.totalMileageReimbursement.toFixed(2),
        `"${billingCodes}"`,
      ].join(',');
    });

    return [header, ...rows].join('\n');
  }
}

export const timesheetService = new TimesheetService();
